import { ColoursObj, Icon } from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useMemo } from 'react'
import { useEditSectionContext } from '../../../feat/editItinerary/editSectionProvider'
import { cardTranslation } from '../../../i18n/translateHelpers'
import { useFormatValue } from '../../hooks/useFormatValue'
import { useScreenTypeContext } from '../../state/screenTypeProvider'
import { Car } from '../interfaces/parking'
import { useObjectContext } from '../providers/objectProvider'
import { CardText } from '../subComponents/cardText'
import { ChatButton } from '../subComponents/chatButton'
import { EditButton } from '../subComponents/editButton'
import { ExpandableSection } from '../subComponents/expendableSection'
import { RowInfo } from '../subComponents/rowInfo'
import { RowInfoNotes } from '../subComponents/rowInfoNotes'
import { ParkingAttachments } from './parkingAttachment'
import { ParkingDriver } from './parkingDriver'
import { ParkingPassengers } from './parkingPassenger'

interface IParkingCarsProps {
    cars: Car[]
    parkingName?: string
}

//cars are already filtered at this point

export const ParkingCars = ({ parkingName, cars }: IParkingCarsProps) => {
    const { screenType } = useScreenTypeContext()
    const { section } = useEditSectionContext()
    const { _id } = useObjectContext()
    const { past } = useObjectContext()
    const { formatValue } = useFormatValue()
    const edit = useMemo(() => {
        if (screenType === 'edit')
            return <EditButton path={`/edit/Section/${section?.id}/${_id}`} />
        return null
    }, [screenType, section, _id])

    const teamPage = useMemo(() => {
        if (screenType === 'team' || screenType === 'teamMember') return true
        else return false
    }, [screenType])

    return (
        <Stack direction={'column'} gap={'8px'}>
            <Stack direction={'row'} justifyContent={'space-between'}>
                <CardText variant="bodyBold">
                    {cardTranslation('Cars')}
                </CardText>
                {edit}
            </Stack>
            {cars &&
                cars.map((car, index) => (
                    <Stack
                        key={index}
                        direction={'row'}
                        gap={'8px'}
                        display={'grid'}
                        gridTemplateColumns={'1fr'}
                    >
                        <ExpandableSection
                            end={
                                screenType === 'edit' ? null : (
                                    <ChatButton
                                        names={[parkingName ?? 'Unknown Parking', car?.number_plate ?? 'Unknown Plate']}
                                        _id={car._id}
                                        editID={_id}
                                        depth={2}
                                        users={[
                                            ...(car?.passengers ?? []),
                                            ...(car?.driver ?? []),
                                        ]}
                                    ></ChatButton>
                                )
                            }
                            mid={
                                <CardText variant="body1">
                                    {car.number_plate}
                                </CardText>
                            }
                        >
                            <Stack spacing={'8px'}>
                                {car.entry && (
                                    <RowInfo
                                        start={
                                            <Icon
                                                name={'Right'}
                                                bubble
                                                bubbleColour={
                                                    past
                                                        ? ColoursObj.shades
                                                            .shade2
                                                        : ColoursObj.core
                                                            .secondary
                                                }
                                            />
                                        }
                                        middle={
                                            <CardText>
                                                {cardTranslation('Entry')}
                                            </CardText>
                                        }
                                        end={
                                            <CardText>
                                                {formatValue(
                                                    car.entry,
                                                    'datetime'
                                                )}
                                            </CardText>
                                        }
                                    />
                                )}
                                {car.exit && (
                                    <RowInfo
                                        start={
                                            <Icon
                                                name={'Left'}
                                                bubble
                                                bubbleColour={
                                                    past
                                                        ? ColoursObj.shades
                                                            .shade2
                                                        : ColoursObj.core
                                                            .secondary
                                                }
                                            />
                                        }
                                        middle={
                                            <CardText>
                                                {cardTranslation('Exit')}
                                            </CardText>
                                        }
                                        end={
                                            <CardText>
                                                {formatValue(
                                                    car.exit,
                                                    'datetime'
                                                )}
                                            </CardText>
                                        }
                                    />
                                )}
                                {!teamPage &&
                                    (car.booking_reference ||
                                        car.notes ||
                                        car.documents) ? (
                                    <CardText variant="bodyBold">
                                        {' '}
                                        {cardTranslation('Car Info')}{' '}
                                    </CardText>
                                ) : null}
                                {car.booking_reference && !teamPage && (
                                    <RowInfo
                                        start={
                                            <Icon
                                                name={'Car_Type'}
                                                bubble
                                                bubbleColour={
                                                    past
                                                        ? ColoursObj.shades
                                                            .shade2
                                                        : ColoursObj.coreBrights
                                                            .primary
                                                }
                                            />
                                        }
                                        middle={
                                            <CardText variant="body1">
                                                {cardTranslation(
                                                    'Booking Reference'
                                                )}
                                            </CardText>
                                        }
                                        end={
                                            <CardText>
                                                {car.booking_reference}
                                            </CardText>
                                        }
                                    />
                                )}
                                {car.notes && !teamPage && (
                                    <RowInfoNotes notes={car.notes} />
                                )}
                                {car.documents && !teamPage && (
                                    <ParkingAttachments
                                        extra_docs={car.documents}
                                    />
                                )}
                                {car.driver && (
                                    <ParkingDriver driver={car.driver} />
                                )}
                                {car.passengers && (
                                    <ParkingPassengers
                                        passengers={car.passengers}
                                    />
                                )}
                            </Stack>
                        </ExpandableSection>
                    </Stack>
                ))}
        </Stack>
    )
}
